// src/components/billing/PlanGate.tsx
// Guardia única de plan. Usa `planIncludes`, así que un plan superior
// (p. ej. STUDIO_FIRM) abre automáticamente todo lo de los inferiores.
import type { ReactNode } from 'react';
import { Lock, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useConfigStore } from '@/store/configStore';
import { NARANJA_PLUS_DEV_OVERRIDE } from '@/lib/naranjaPlusRules';
import { planIncludes, normalizePlan, PLAN_LABELS } from '@/config/plans';
import type { PlanType } from '@/db/db';

interface PlanGateProps {
  required: PlanType;
  title: string;
  description: string;
  children: ReactNode;
}

export const PlanGate = ({ required, title, description, children }: PlanGateProps) => {
  const { settings } = useConfigStore();
  const current = settings?.plan_type ?? 'FREE';
  const allowed = NARANJA_PLUS_DEV_OVERRIDE || planIncludes(current, required);

  if (allowed) return <>{children}</>;

  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="w-full max-w-md rounded-3xl border border-amber-200 bg-white p-8 text-center shadow-xl dark:border-amber-900/40 dark:bg-slate-900">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-500/10 text-amber-500">
          <Lock size={28} />
        </div>
        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-amber-500">
          Requiere {PLAN_LABELS[required]}
        </p>
        <h1 className="mt-1 text-2xl font-black text-slate-900 dark:text-white">{title}</h1>
        <p className="mt-3 text-sm text-slate-500">{description}</p>
        <p className="mt-2 rounded-xl bg-amber-50 p-3 text-xs font-bold text-amber-700 dark:bg-amber-950/20 dark:text-amber-300">
          Tu plan actual: {PLAN_LABELS[normalizePlan(current)]}
        </p>
        <Link
          to="/mi-plan"
          className="mt-5 inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-black text-slate-950 hover:bg-amber-400"
        >
          <Sparkles size={16} /> Ver planes y activar
        </Link>
      </div>
    </div>
  );
};

export default PlanGate;
